import React from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { lighten } from '@mui/material/styles';
import { Paper, Button, Backdrop, Grid } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import LinearProgress from "@mui/material/LinearProgress";

import * as DateUtilities from "../../../../utilities/DateUtilities";


const PREFIX = 'DropZoneLoaderTable';

const classes = {
	paper: `${PREFIX}-paper`,
	linearProgress: `${PREFIX}-linearProgress`,
	table: `${PREFIX}-table`,
	tableContainer: `${PREFIX}-tableContainer`,
	visuallyHidden: `${PREFIX}-visuallyHidden`
};

const StyledPaper = styled(Paper)((
	{
		theme
	}
) => ({
	[`&.${classes.paper}`]: {
		height: '100%',
		width: 'calc(100% - 16px)',
		margin: '8px'
	},

	[`& .${classes.linearProgress}`]: {
		height: '1px'
	},

	[`& .${classes.table}`]: {
		minWidth: 750
	},

	[`& .${classes.tableContainer}`]: {
		height: 'calc(100% - 120px)',
		minHeight: '350px',
		borderRadius: 0,
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
	},

	[`& .${classes.visuallyHidden}`]: {
		border: 0,
		clip: 'rect(0 0 0 0)',
		height: 1,
		margin: -1,
		overflow: 'hidden',
		padding: 0,
		position: 'absolute',
		top: 20,
		width: 1,
	}
}));

let rows = [
    // Sample
    // createData('', '', '', ''),
];

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
}

const headCells = [
    { id: 'Description', numeric: false, disablePadding: false, label: 'Description' },
	{ id: 'ProjectID', numeric: false, disablePadding: false, label: 'ProjectID' },
    { id: 'Status', numeric: false, disablePadding: false, label: 'Status' },
    { id: 'Success', numeric: false, disablePadding: false, label: 'Success' },
    { id: 'Notes', numeric: false, disablePadding: false, label: 'Notes' },
    { id: 'RunStartDateTime', numeric: false, disablePadding: false, label: 'Started At' },
    { id: 'Import_Duration', numeric: false, disablePadding: false, label: 'Duration' },
    { id: 'LoadedBy', numeric: false, disablePadding: false, label: 'Performed By' }
];

function EnhancedTableHead(props) {
    const {  order, orderBy, rowCount, onRequestSort } = props;
    const createSortHandler = property => event => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {headCells.map(headCell => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <span className={classes.visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </span>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    classes: PropTypes.object.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};


const PREFIX2 = 'EnhancedTableToolbar';

const classes2 = {
	root: `${PREFIX2}-root`,
	title: `${PREFIX2}-title`
};

const StyledEnhancedTableToolbar = styled(Toolbar)((
    {
        theme
    }
) => ({
    [`&.${classes2.root}`]: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
    },

    [`& .${classes2.title}`]: {
        flex: '1 1 100%',
    }
}));

const EnhancedTableToolbar = props => {
    const classes = useToolbarStyles();
    const { loaderType } = props;

    return (
		<StyledEnhancedTableToolbar className={clsx(classes2.root)}>
			{(
				<Typography className={classes2.title} variant="h6" id="tableTitle">
					{loaderType}
				</Typography>
			)}
		</StyledEnhancedTableToolbar>
    );
};

export default function EnhancedTable({loading, data, importID, importTime} ) {


	// Component State
    const [order, setOrder] = React.useState('desc');
    const [orderBy, setOrderBy] = React.useState('RunStartDateTime');
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    // const [rowState, setRowState] = React.useState(0);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

	return (
        <React.Fragment>
            <StyledPaper className={classes.paper}>
                {/*<EnhancedTableToolbar loaderType={loaderType} />*/}
                {loading &&
                    <LinearProgress className={classes.linearProgress}/>
                }
                <TableContainer className={classes.tableContainer}>
                    <Table
                        className={classes.table}
                        aria-labelledby="tableTitle"
                        size={'medium'}
                        aria-label="enhanced table"
                    >
                        <EnhancedTableHead
                                classes={classes}
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                rowCount={data != null ? data.length : 0}
                            />
                        <TableBody>
                        <React.Fragment>
                            {data &&
                                <React.Fragment>
                                    {stableSort(data, getComparator(order, orderBy))
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map(row => (
                                        <TableRow hover tabIndex={-1} key={row.ID}>
                                            <TableCell component="th" scope="row" align="left" style={{ width: 210 }}>{row.Description}</TableCell>
											<TableCell align="left" style={{ width: 75 }}>{row.ProjectID}</TableCell>
											<TableCell align="left" style={{ width: 125 }}>{row.Status}</TableCell>
                                            <TableCell align="left" style={{ width: 75 }}>{row.Success}</TableCell>
                                            <TableCell align="left" style={{ width: 210 }}>{row.ErrorMessage}</TableCell>
                                            <TableCell align="left" style={{ width: 125 }}>{DateUtilities.dateTimeString(row.RunStartDateTime)}</TableCell>
                                            <TableCell align="left" style={{ width: 125 }}>
                                                {loading && importID === row.ID &&
                                                    importTime.toString()
                                                }
                                                {row.Import_Duration &&
                                                    row.Import_Duration
                                                }
                                            </TableCell>
                                            <TableCell align="left" style={{ width: 160 }}>{row.LoadedBy}</TableCell>
                                        </TableRow>
                                        ))}
                                </React.Fragment>
                            }
                            </React.Fragment>
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={data != null ? data.length : 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </StyledPaper>
		</React.Fragment>
    );
}
